import { AdminAlarms } from "./AdminAlarms";
import { AdminCarbonCropList } from "portal/components/carbonCrop/AdminCarbonCropList";
import { AdminCarbonCropPage } from "portal/components/carbonCrop/AdminCarbonCropPage";
import { AdminConfigBulk } from "./AdminConfigBulk";
import { AdminConfigTemplates } from "./AdminConfigTemplates";
import { AdminPlantProfileList } from "./AdminPlantProfileList";
import { AdminPortal } from "./AdminPortal";
import {
  AdminSubpath,
  getAdminPath,
  getAdminPlantProfilePath,
  Slug,
} from "portal/utils/routing";
import { AlmanacList } from "portal/components/almanac/AlmanacList";
import {
  anyContainingPermission,
  useAuthorizationRequired,
  withAuthorizationRequired,
} from "portal/components/auth/WithAuthorizationRequired";
import { buildPermission } from "portal/utils/auth";
import { Header } from "portal/components/header/Header";
import {
  Navigate,
  RouteObject,
  useLocation,
  useParams,
  useRoutes,
} from "react-router-dom";
import { Page } from "portal/components/Page";
import {
  PermissionAction,
  PermissionDomain,
  PermissionResource,
} from "protos/portal/auth";
import { PlantProfile } from "portal/components/categoryCollectionProfile/PlantProfile";
import { portalEndpoints } from "../../state/portalApi";
import { ResponsiveSubnav } from "portal/components/ResponsiveSubnav";
import { RobotClass } from "portal/utils/robots";
import { titleCase } from "portal/utils/strings";
import { UnauthorizedPage } from "portal/components/UnauthorizedPage";
import { useCanSeeAdminCarbonCrops } from "./permissions";
import { useCanSeeAdminPlantProfiles } from "portal/views/plantProfiles/permissions";
import { useTranslation } from "react-i18next";
import { VelocityEstimatorList } from "portal/components/velocityEstimator/VelocityEstimatorList";
import React, { FC, FunctionComponent, useState } from "react";

const _Admin: FC = () => {
  const { t } = useTranslation();

  const [subtitle, setSubtitle] = useState<string>(t("views.admin.title"));
  const routes: RouteObject[] = [];

  const isConfigTemplatesAuthorized = useAuthorizationRequired(
    buildPermission(
      PermissionAction.read,
      PermissionResource.configs,
      PermissionDomain.templates
    )
  );
  if (isConfigTemplatesAuthorized) {
    routes.push(
      {
        path: `${AdminSubpath.CONFIG_TEMPLATES}/:class/*`,
        element: <AdminConfigTemplates />,
      },
      {
        path: `${AdminSubpath.CONFIG_TEMPLATES}`,
        element: (
          <Navigate
            to={`${getAdminPath(AdminSubpath.CONFIG_TEMPLATES)}/${
              RobotClass.Slayers
            }`}
            replace
          />
        ),
      },
      {
        path: `${AdminSubpath.CONFIG}/*`,
        element: (
          <ConfigTemplatesRedirect
            basePath={getAdminPath(AdminSubpath.CONFIG)}
          />
        ),
      }
    );
  }

  const isConfigBulkAuthorized = useAuthorizationRequired(
    buildPermission(
      PermissionAction.update,
      PermissionResource.configs,
      PermissionDomain.all
    )
  );
  if (isConfigBulkAuthorized) {
    routes.push({
      path: AdminSubpath.CONFIG_BULK,
      element: <AdminConfigBulk />,
    });
  }

  const isAlarmsAuthorized = useAuthorizationRequired(
    buildPermission(
      PermissionAction.read,
      PermissionResource.admin_alarms,
      PermissionDomain.all
    )
  );
  if (isAlarmsAuthorized) {
    routes.push({ path: AdminSubpath.ALARMS, element: <AdminAlarms /> });
  }

  const isAlmanacsAuthorized = useAuthorizationRequired(
    buildPermission(
      PermissionAction.read,
      PermissionResource.almanacs,
      PermissionDomain.templates
    )
  );
  if (isAlmanacsAuthorized) {
    routes.push({
      path: `${AdminSubpath.ALMANAC}/*`,
      element: (
        <AlmanacList
          adminEditing
          basePath={getAdminPath(AdminSubpath.ALMANAC)}
          useListQuery={portalEndpoints.listGlobalAlmanacs.useQuery}
          useGetQuery={portalEndpoints.getGlobalAlmanac.useQuery}
          useDeleteMutation={portalEndpoints.deleteGlobalAlmanac.useMutation}
          useSetMutation={portalEndpoints.setGlobalAlmanac.useMutation}
        />
      ),
    });
  }

  const isVelocityEstimatorsAuthorized = useAuthorizationRequired(
    buildPermission(
      PermissionAction.read,
      PermissionResource.velocity_estimators,
      PermissionDomain.templates
    )
  );
  if (isVelocityEstimatorsAuthorized) {
    routes.push({
      path: `${AdminSubpath.VELOCITY_ESTIMATOR}/*`,
      element: (
        <VelocityEstimatorList
          adminEditing
          basePath={getAdminPath(AdminSubpath.VELOCITY_ESTIMATOR)}
          useListQuery={
            portalEndpoints.listGlobalTargetVelocityEstimators.useQuery
          }
          useGetQuery={
            portalEndpoints.getGlobalTargetVelocityEstimator.useQuery
          }
          useSetMutation={
            portalEndpoints.setGlobalTargetVelocityEstimator.useMutation
          }
          useDeleteMutation={
            portalEndpoints.deleteGlobalTargetVelocityEstimator.useMutation
          }
        />
      ),
    });
  }

  const isCategoryCollectionProfilesAuthorized = useCanSeeAdminPlantProfiles();
  if (isCategoryCollectionProfilesAuthorized) {
    routes.push(
      {
        path: `${AdminSubpath.PLANT_PROFILE}/${Slug.ID}/${Slug.EDIT}?`,
        element: <AdminPlantProfile />,
      },
      {
        path: AdminSubpath.PLANT_PROFILE,
        element: <AdminPlantProfileList />,
      }
    );
  }

  const isCarbonCropsAuthorized = useCanSeeAdminCarbonCrops();
  if (isCarbonCropsAuthorized) {
    routes.push(
      {
        path: AdminSubpath.CARBON_CROPS,
        element: <AdminCarbonCropList />,
      },
      {
        path: `${AdminSubpath.CARBON_CROPS}/${Slug.ID}`,
        element: <AdminCarbonCropPage />,
      }
    );
  }

  const isPortalAuthorized = useAuthorizationRequired(
    buildPermission(
      PermissionAction.read,
      PermissionResource.admin_cloud,
      PermissionDomain.all
    )
  );
  if (isPortalAuthorized) {
    routes.push({
      path: AdminSubpath.PORTAL,
      element: <AdminPortal />,
    });
  }

  // pick a default path based on what the user has access to
  let defaultPath: AdminSubpath = AdminSubpath.CONFIG_TEMPLATES;
  if (isConfigTemplatesAuthorized) {
    defaultPath = AdminSubpath.CONFIG_TEMPLATES;
  } else if (isConfigBulkAuthorized) {
    defaultPath = AdminSubpath.CONFIG_BULK;
  } else if (isAlarmsAuthorized) {
    defaultPath = AdminSubpath.ALARMS;
  } else if (isAlmanacsAuthorized) {
    defaultPath = AdminSubpath.ALMANAC;
  } else if (isVelocityEstimatorsAuthorized) {
    defaultPath = AdminSubpath.VELOCITY_ESTIMATOR;
  } else if (isCategoryCollectionProfilesAuthorized) {
    defaultPath = AdminSubpath.PLANT_PROFILE;
  } else if (isCarbonCropsAuthorized) {
    defaultPath = AdminSubpath.CARBON_CROPS;
  } else if (isPortalAuthorized) {
    defaultPath = AdminSubpath.PORTAL;
  }
  routes.push({
    path: "*",
    element: <Navigate to={getAdminPath(defaultPath)} replace />,
  });

  const element = useRoutes(routes);

  // if user has no access to child views, don't render anything
  if (
    !isConfigTemplatesAuthorized &&
    !isConfigBulkAuthorized &&
    !isAlarmsAuthorized &&
    !isAlmanacsAuthorized &&
    !isVelocityEstimatorsAuthorized &&
    !isCategoryCollectionProfilesAuthorized &&
    !isCarbonCropsAuthorized &&
    !isPortalAuthorized
  ) {
    return <UnauthorizedPage />;
  }

  return (
    <>
      <Header title={subtitle}>
        <ResponsiveSubnav
          onChange={(index, name) => setSubtitle(name)}
          pages={[
            {
              label: titleCase(t("models.configs.template_other")),
              to: `/admin${AdminSubpath.CONFIG_TEMPLATES}`,
              hidden: !isConfigTemplatesAuthorized,
            },
            {
              label: titleCase(t("views.admin.config.bulk.title")),
              to: `/admin${AdminSubpath.CONFIG_BULK}`,
              hidden: !isConfigBulkAuthorized,
            },
            {
              label: titleCase(t("models.alarms.alarm_other")),
              to: `/admin${AdminSubpath.ALARMS}`,
              hidden: !isAlarmsAuthorized,
            },
            {
              label: titleCase(t("models.almanacs.almanac_other")),
              to: `/admin${AdminSubpath.ALMANAC}`,
              hidden: !isAlmanacsAuthorized,
            },
            {
              label: titleCase(
                t("models.velocityEstimators.velocityEstimator_other")
              ),
              to: `/admin${AdminSubpath.VELOCITY_ESTIMATOR}`,
              hidden: !isVelocityEstimatorsAuthorized,
            },
            {
              label: t(
                "models.categoryCollectionProfiles.categoryCollectionProfile_title_other"
              ),
              to: getAdminPlantProfilePath(),
              hidden: !isCategoryCollectionProfilesAuthorized,
            },
            {
              label: t("models.carbonCrops.carbonCrop_title_other"),
              to: `/admin${AdminSubpath.CARBON_CROPS}`,
              hidden: !isCarbonCropsAuthorized,
            },
            {
              label: titleCase(t("views.admin.portal.title")),
              to: `/admin${AdminSubpath.PORTAL}`,
              hidden: !isPortalAuthorized,
            },
          ]}
        />
      </Header>
      <Page>{element}</Page>
    </>
  );
};

interface ConfigTemplatesRedirectProps {
  basePath: string;
}
const ConfigTemplatesRedirect: FunctionComponent<
  ConfigTemplatesRedirectProps
> = ({ basePath }) => {
  const { pathname } = useLocation();
  const subpath = pathname.startsWith(basePath)
    ? pathname.slice(basePath.length)
    : "";
  return (
    <Navigate
      to={`${getAdminPath(AdminSubpath.CONFIG_TEMPLATES)}${subpath}`}
      replace
    />
  );
};

const AdminPlantProfile: FC = () => {
  const { id } = useParams<{ id: string }>();
  if (!id) {
    return;
  }
  return (
    <PlantProfile
      isAdmin
      uuid={id}
      parentLink={getAdminPlantProfilePath()}
      useSetMutation={
        portalEndpoints.applyGlobalPlantProfileChanges.useMutation
      }
    />
  );
};

export const Admin = withAuthorizationRequired(
  anyContainingPermission(
    PermissionAction.read,
    PermissionResource.admin_cloud,
    PermissionDomain.all
  ),
  _Admin
);
